import { Check, ChevronDown } from "lucide-react";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  MARKET_COUNTRIES,
  MARKET_COUNTRY_LABELS,
  MARKET_COUNTRY_SWITCHER_TRANSLATIONS,
  commitMarketCountrySelection,
  getTranslation,
  readStoredMarketCountry,
  useIsMultiLanguageEnabled,
  useLocale,
  type MarketCountry,
  type MivoTranslationKey,
} from "@mivo/i18n";

export type CountryChromeProps = {
  className?: string;
  onPersistLocale?: (locale: MivoTranslationKey) => Promise<void>;
  onPersistMarketCountry?: (marketCountry: MarketCountry) => Promise<void>;
  "data-testid"?: string;
};

export function CountryChrome({
  className,
  onPersistLocale,
  onPersistMarketCountry,
  "data-testid": dataTestId = "country-switcher",
}: CountryChromeProps) {
  const enabled = useIsMultiLanguageEnabled();
  const { locale } = useLocale();
  const [marketCountry, setMarketCountry] = useState<MarketCountry>(() => readStoredMarketCountry());
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const filterRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const listboxId = useId();
  const label = getTranslation(MARKET_COUNTRY_SWITCHER_TRANSLATIONS.label);
  const filterPlaceholder = getTranslation(MARKET_COUNTRY_SWITCHER_TRANSLATIONS.filterPlaceholder);
  const emptyLabel = getTranslation(MARKET_COUNTRY_SWITCHER_TRANSLATIONS.empty);

  const options = useMemo(
    () =>
      MARKET_COUNTRIES.map((country) => ({
        value: country,
        label: MARKET_COUNTRY_LABELS[country],
      })),
    [],
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) {
      return options;
    }
    return options.filter((option) => option.label.toLocaleLowerCase().includes(normalizedQuery));
  }, [options, query]);

  useEffect(() => {
    if (!open) {
      return;
    }
    filterRef.current?.focus();
    const selectedIndex = filteredOptions.findIndex((option) => option.value === marketCountry);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [open, filteredOptions, marketCountry]);

  if (!enabled) {
    return null;
  }

  const handleChange = async (nextCountry: MarketCountry) => {
    const result = await commitMarketCountrySelection({
      nextCountry,
      previousCountry: marketCountry,
      currentLocale: locale,
      onPersistLocale,
      onPersistMarketCountry,
    });
    if (result !== "unchanged") {
      setMarketCountry(nextCountry);
    }
    setOpen(false);
    setQuery("");
  };

  const moveActive = (delta: number) => {
    if (filteredOptions.length === 0) {
      return;
    }
    setActiveIndex((current) => {
      const next = (current + delta + filteredOptions.length) % filteredOptions.length;
      optionRefs.current[next]?.focus();
      return next;
    });
  };

  const onOptionKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveActive(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(-1);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
      optionRefs.current[0]?.focus();
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      const last = filteredOptions.length - 1;
      setActiveIndex(last);
      optionRefs.current[last]?.focus();
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const option = filteredOptions[index];
      if (option) {
        void handleChange(option.value);
      }
    }
  };

  const onFilterKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (filteredOptions.length > 0) {
        setActiveIndex(0);
        optionRefs.current[0]?.focus();
      }
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const option = filteredOptions[activeIndex] ?? filteredOptions[0];
      if (option) {
        void handleChange(option.value);
      }
    }
  };

  const selectedLabel = MARKET_COUNTRY_LABELS[marketCountry];

  return (
    <div className={cn("inline-flex h-8 items-center", className)} data-testid={dataTestId}>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setQuery("");
          }
        }}
      >
        <PopoverTrigger
          data-testid={`${dataTestId}-trigger`}
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          className={cn(
            "inline-flex h-8 max-w-[11rem] items-center gap-1.5 self-center rounded-lg border border-border bg-background px-2.5 text-xs font-semibold leading-none text-foreground shadow-none outline-none transition hover:bg-muted focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
          )}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setOpen(true);
            }
          }}
        >
          <span className="min-w-0 truncate">{selectedLabel}</span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </PopoverTrigger>
        <PopoverContent
          id={listboxId}
          align="end"
          role="listbox"
          aria-label={label}
          data-testid={`${dataTestId}-popover`}
          className="w-[min(16rem,calc(100vw-2rem))] gap-1 p-1"
        >
          <Input
            ref={filterRef}
            type="search"
            value={query}
            data-testid={`${dataTestId}-filter`}
            aria-label={filterPlaceholder}
            placeholder={filterPlaceholder}
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onFilterKeyDown}
          />
          <div className="flex max-h-60 flex-col gap-0.5 overflow-y-auto p-0.5">
            {filteredOptions.length === 0 ? (
              <p
                data-testid={`${dataTestId}-empty`}
                className="px-1.5 py-1 text-sm text-muted-foreground"
              >
                {emptyLabel}
              </p>
            ) : (
              filteredOptions.map((option, index) => {
                const isActive = option.value === marketCountry;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    ref={(element) => {
                      optionRefs.current[index] = element;
                    }}
                    tabIndex={index === activeIndex ? 0 : -1}
                    data-testid={`${dataTestId}-${option.value}`}
                    aria-selected={isActive}
                    className={cn(
                      "flex w-full min-h-9 items-center gap-2 rounded-md px-1.5 py-1 text-left text-sm text-foreground outline-none transition hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-ring",
                      isActive ? "bg-muted/60 font-medium" : "font-normal",
                    )}
                    onClick={() => {
                      void handleChange(option.value);
                    }}
                    onKeyDown={(event) => {
                      onOptionKeyDown(event, index);
                    }}
                  >
                    <span
                      className="flex size-4 shrink-0 items-center justify-center"
                      aria-hidden={!isActive}
                    >
                      {isActive ? <Check className="size-4" /> : null}
                    </span>
                    <span>{option.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
